import {useApp} from "../../app.context";
import {useMemo, useState} from "react";
import AssetImage from "../../elements/assetImage.element";
import NumberInputLabel from "../../elements/numberInputLabel.element";
import {useIntegrator} from "../../integrator.context";
import {AddImageCommand} from "../../commands/addImage.command";
import {RemoveImageCommand} from "../../commands/removeImage.command";
import {UpdateImageCommand} from "../../commands/updateImage.command";
import {SetImageDisplayNameCommand} from "../../commands/setImageDisplayName.command";
import {makeImageLabelResolver} from "../../utils/imageLabels";
import UploadButton from "../../elements/uploadButton.element";


function ImagesEditorView() {
    const {state, executeCommand} = useIntegrator();
    const {setNotification, getImageUrl, getLocalizationValue, confirmDelete} = useApp();
    const [newKey, setNewKey] = useState<string>('');
    const [previewImage, setPreviewImage] = useState<string | undefined>(undefined);
    const [editingKey, setEditingKey] = useState<string | undefined>(undefined);

    // Same resolver the picker uses, so an image shows the same friendly
    // name in both places (e.g. "Archer" derived from the pack).
    const resolver = useMemo(
        () => makeImageLabelResolver(state, getLocalizationValue),
        [state, getLocalizationValue]
    );

    function saveName(key: string, value: string) {
        executeCommand(new SetImageDisplayNameCommand(key, value));
        setEditingKey(undefined);
    }

    function addNewImage() {
        if (newKey === '') {
            setNotification({
                title: 'Invalid Input',
                message: 'Please enter a valid key.'
            })
            return;
        }

        if (newKey in state.images) {
            setNotification({
                title: 'Key Already Exists',
                message: 'Please enter a new key.'
            })
            return;
        }

        executeCommand(new AddImageCommand(newKey));
        setNewKey('');
    }

    return (
        <div className={'layout vertical'} style={{gap: 10, flexGrow: 1}}>
            <div className={'grid two-columns gap'} style={{gap: 10, flexGrow: 1, overflow: 'auto', height: 0}}>
                {state.images !== undefined && Object.keys(state.images).map((image, index) => {
                    const value = state.images[image];
                    const {label: displayLabel, explicit, orphan} = resolver.labelFor(image);
                    const isEditing = editingKey === image;
                    return (
                        <div key={index} className={'box'}>
                            <div className={'layout horizontal gap'} style={{gap: 20}}>
                                <AssetImage id={image} size={80} upload={false}
                                            onClick={() => setPreviewImage(image)}
                                            style={{cursor: 'pointer'}}/>
                                <div className={'layout vertical'} style={{width: 240, gap: 2}}>
                                    {isEditing ? (
                                        <input autoFocus
                                               defaultValue={displayLabel}
                                               onFocus={e => e.currentTarget.select()}
                                               onBlur={e => {
                                                   const v = e.currentTarget.value;
                                                   // Click-and-blur with no edit on a never-named
                                                   // image is a no-op — don't flag it explicit.
                                                   if (!explicit && v === displayLabel) {
                                                       setEditingKey(undefined);
                                                       return;
                                                   }
                                                   saveName(image, v);
                                               }}
                                               onKeyDown={e => {
                                                   if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur();
                                                   else if (e.key === 'Escape') setEditingKey(undefined);
                                               }}
                                               style={{
                                                   fontSize: 'var(--font-size-default)',
                                                   boxSizing: 'border-box', width: '100%',
                                                   padding: '1px 2px',
                                                   border: '1px solid #515151',
                                               }}/>
                                    ) : (
                                        <p style={{
                                            fontSize: 'var(--font-size-default)',
                                            boxSizing: 'border-box', width: '100%',
                                            padding: '1px 2px',
                                            border: '1px solid transparent',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                            fontStyle: explicit ? 'normal' : 'italic',
                                            fontWeight: explicit ? 600 : 400,
                                            cursor: 'text',
                                        }}
                                           title={explicit ? 'Click to rename' : 'Click to name this image'}
                                           onClick={() => setEditingKey(image)}>
                                            {displayLabel}{orphan ? ' •' : ''}
                                        </p>
                                    )}
                                    <p style={{
                                        fontSize: 12,
                                        color: 'rgba(255,255,255,0.66)',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                    }} title={image}>
                                        {image}
                                    </p>
                                </div>
                                <div className={'layout horizontal gap'} style={{alignItems: 'flex-start'}}>
                                    <UploadButton id={image}/>
                                    <button onClick={async () => {
                                        if (await confirmDelete(displayLabel, 'image'))
                                            executeCommand(new RemoveImageCommand(image));
                                    }}>
                                        <span className={'fa-solid fa-trash'}/>
                                    </button>
                                </div>
                                <div className={'layout vertical gap'}>
                                    <p className={'text header'}>Import Settings</p>
                                    <NumberInputLabel title={'Pixels Per Unit'} value={value.pixelMultiplier} onChange={v => {
                                        value.pixelMultiplier = v;
                                        executeCommand(new UpdateImageCommand(image, value));
                                    }}/>
                                    <NumberInputLabel title={'Pivot X'} value={value.offsetX} onChange={v => {
                                        value.offsetX = v;
                                        executeCommand(new UpdateImageCommand(image, value));
                                    }}/>
                                    <NumberInputLabel title={'Pivot Y'} value={value.offsetY} onChange={v => {
                                        value.offsetY = v;
                                        executeCommand(new UpdateImageCommand(image, value));
                                    }}/>
                                </div>
                            </div>
                        </div>
                    )
                })}
            </div>
            <input placeholder={'Enter key...'} value={newKey} onChange={v => setNewKey(v.target.value)}/>
            <button onClick={addNewImage}>Add New Image</button>
            {previewImage && (
                <div className={'layout vertical'} onClick={() => setPreviewImage(undefined)}
                     style={{
                         position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                         backgroundColor: 'rgba(0, 0, 0, 0.8)',
                         alignItems: 'center', justifyContent: 'center',
                         display: 'flex', zIndex: 1000
                     }}>
                    <div style={{
                        border: '3px solid magenta',
                        borderRadius: 0,
                        backgroundImage: 'repeating-conic-gradient(#808080 0% 25%, #404040 0% 50%)',
                        backgroundSize: '16px 16px',
                    }}
                         onClick={e => e.stopPropagation()}>
                        <img alt={previewImage} src={getImageUrl(previewImage)}
                             style={{imageRendering: 'pixelated', display: 'block'}}/>
                    </div>
                </div>
            )}
        </div>
    )
}

export default ImagesEditorView;